import { PlayingAnimation } from '@/components/animations/playing-animation';
import { isModernBanner } from '@/components/card';
import { CardVariant } from '@/components/card/types';
import { columnsToWidth } from '@/components/ui/layout';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { useTrackRowPlayback } from '@/hooks/use-track-row-playback';
import { isCtaToggleEligible } from '@/utils/media/cta-toggle';
import { EnumButtonName } from '@calm/evexia-analytics';
import { useProvidedProgramContextWithWorkflow } from '@rhg/hooks/use-provided-program-context/with-workflow';
import commonMessages from '@rhg/lang/messages/common';
import { Box, Button } from '@rhg/libs/material';
import { PlayFilled, VideoFilled } from '@rhg/libs/material/icons';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { ProgramContextAwareSessionCardProps } from '../program-context-aware';
import { useSessionCardPlayHandler } from '../program-context-aware/use-play-handler';
import { useVariantAgnosticSessionCardProps } from '../program-context-aware/use-variant-agnostic-session-card-props';
import { useVariantAwareSessionCardProps } from '../program-context-aware/use-variant-aware-session-card-props';

const styleBannerPlayButton = {
    width: { sm: '100%', md: 'auto' },
    maxWidth: columnsToWidth(2),
};

// 22px to match the sibling start icons (PlayFilled/VideoFilled). MUI's Button
// `commonIconStyles` sizes a startIcon's first child to font-size 22 on
// size="large" (18 small / 20 medium / 22 large); MUI SvgIcons render at 1em =
// 22px. PlayingAnimation renders a Box>svg that sizes off width/height, not
// font-size, so we constrain it explicitly to the same 22px.
const stylePlayingAnimation = {
    '& svg': {
        maxHeight: 22,
        maxWidth: 22,
    },
};

interface SessionCardBannerButtonStartIconProps {
    variant: CardVariant;
    isPlaying: boolean;
    isVideo: boolean;
}

/** The play button's leading icon, by playback + content state. */
function SessionCardBannerButtonStartIcon({
    variant,
    isPlaying,
    isVideo,
}: SessionCardBannerButtonStartIconProps) {
    if (isModernBanner(variant) && isPlaying) {
        return (
            <Box sx={stylePlayingAnimation}>
                <PlayingAnimation />
            </Box>
        );
    }
    if (isVideo) {
        return <VideoFilled />;
    }
    return <PlayFilled />;
}

type SessionCardBannerButtonProps = Pick<
    ProgramContextAwareSessionCardProps,
    | 'variant'
    | 'session'
    | 'sessionIndex'
    | 'categoryTitle'
    | 'trackProps'
    | 'onPlay'
    | 'progressSource'
>;
export function SessionCardBannerButton({
    variant,
    session,
    sessionIndex,
    categoryTitle: _categoryTitle,
    trackProps,
    onPlay,
    progressSource,
}: SessionCardBannerButtonProps) {
    const { formatMessage } = useIntl();
    const isMobile = useIsMobile();
    const isBannerV3Mobile = variant === CardVariant.BannerV3 && isMobile;
    const buttonSize = isBannerV3Mobile ? 'small' : 'large';

    const { isVideo } = useVariantAgnosticSessionCardProps({
        session,
        sessionIndex,
        categoryTitle: _categoryTitle,
    });

    const { buttonAriaLabel } = useVariantAwareSessionCardProps({
        variant,
        session,
        sessionIndex,
        categoryTitle: _categoryTitle,
        trackProps,
        onPlay,
        progressSource,
        additionalAction: undefined,
        imageIconTop: undefined,
    });

    const { playHandler } = useSessionCardPlayHandler({
        session,
        trackProps,
        onPlay,
        progressSource,
    });

    const { programType } = useProvidedProgramContextWithWorkflow();
    const { isActiveTrack, isPlaying, toggle } = useTrackRowPlayback(session);
    const toggleEligible = isCtaToggleEligible(session, programType);

    const handleClick = useCallback(() => {
        // On the active eligible track the button pauses/resumes in place;
        // otherwise it opens/starts playback.
        if (isActiveTrack && toggleEligible) {
            toggle();
            return;
        }
        void playHandler();
    }, [isActiveTrack, toggleEligible, toggle, playHandler]);

    return (
        <Button
            trackButton={EnumButtonName.ProgramPlay}
            startIcon={
                <SessionCardBannerButtonStartIcon
                    variant={variant}
                    isPlaying={isPlaying}
                    isVideo={isVideo}
                />
            }
            variant="contained"
            color="primary"
            size={buttonSize}
            fullWidth={isBannerV3Mobile}
            sx={!isBannerV3Mobile ? styleBannerPlayButton : undefined}
            onClick={handleClick}
            aria-label={buttonAriaLabel}
        >
            {variant === CardVariant.Banner || variant === CardVariant.BannerV3
                ? formatMessage(commonMessages['common.simple_play'])
                : buttonAriaLabel}
        </Button>
    );
}
